import { CharlieQrCode } from '@ver.id/libs.ui.charlie';
import { HoruzProtocolChulakServiceSessionGuardTags } from '@ver.id/libs.internal.horuz';
import { useTranslation } from '../../i18n/i18n';
import { type AppInfo, type RequestType } from '../../types';
import EmbeddedLayout from '../../layouts/EmbeddedLayout';
import FlowFooter from '../../components/FlowFooter';

type Props = {
  tags: HoruzProtocolChulakServiceSessionGuardTags;
  app?: AppInfo;
  requestType?: RequestType;
  onCancel: () => void;
  onBack?: () => void;
};

function Step({ number, text }: { number: number; text: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex items-center justify-center size-6 rounded-full bg-primary/10 text-xs font-semibold text-primary">
        {number}
      </span>
      <span className="text-sm text-foreground">{text}</span>
    </div>
  );
}

export default function EmbeddedAgentStartPage({
  tags,
  app,
  requestType,
  onCancel,
  onBack,
}: Props) {
  const { t } = useTranslation();
  const appName = app?.name || 'your identity app';
  const isIssuance = requestType === 'issuance';

  const secondaryLabel = onBack ? t('abort.goBack') : t('agentStart.cancel');
  const onSecondary = onBack ?? onCancel;

  if (tags.type === 'Redirect') {
    const redirectUri = (tags.redirect as { uri: string }).uri;
    return (
      <EmbeddedLayout
        footer={
          <FlowFooter
            primaryLabel={t('agentStart.redirectButton')}
            onPrimary={() => window.location.replace(redirectUri)}
            secondaryLabel={secondaryLabel}
            onSecondary={onSecondary}
          />
        }
      >
        <div className="flex flex-1 items-center justify-center w-full">
          <div className="text-center max-w-lg w-full">
            <h2 className="text-2xl font-semibold text-foreground">
              {t('agentStart.title', { appName })}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t(
                isIssuance
                  ? 'agentStart.issuanceRedirectSubtitle'
                  : 'agentStart.redirectSubtitle',
                { appName },
              )}
            </p>
          </div>
        </div>
      </EmbeddedLayout>
    );
  }

  return (
    <EmbeddedLayout
      footer={
        <FlowFooter secondaryLabel={secondaryLabel} onSecondary={onSecondary} />
      }
    >
      <div className="flex flex-1 items-center justify-center w-full">
        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6 sm:gap-x-10 sm:gap-y-4 items-center max-w-2xl w-full">
          {/* Title / subtitle */}
          <div className="text-center sm:text-left sm:col-start-2 sm:row-start-1">
            <h2 className="text-2xl font-semibold text-foreground">
              {t('agentStart.title', { appName })}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t(
                isIssuance
                  ? 'agentStart.issuanceSubtitle'
                  : 'agentStart.subtitle',
                { appName },
              )}
            </p>
          </div>

          {/* QR code — bare, no card wrapper, capped so it never exceeds the viewport minus the footer */}
          <div className="justify-self-center sm:row-span-2 sm:col-start-1 sm:row-start-1 [&>canvas]:max-w-[min(100%,calc(100dvh-6rem))]">
            <CharlieQrCode
              data={tags.type === 'Qr' ? tags.qr.content : ''}
              image={app?.logo}
            />
          </div>

          {/* Steps */}
          <div className="flex flex-col gap-4 items-center sm:items-start sm:col-start-2 sm:row-start-2">
            <Step number={1} text={t('agentStart.step1', { appName })} />
            <Step number={2} text={t('agentStart.step2', { appName })} />
            <Step number={3} text={t('agentStart.step3', { appName })} />
          </div>
        </div>
      </div>
    </EmbeddedLayout>
  );
}
